import styles from './LocalePicker.module.scss';

import { Image } from 'components/Image/Image';
import React from 'react';
import { useBaseUrlFromLocale } from 'utils/AppRoute';
import { Environment } from 'utils/Environment';
import {
  CountryCode,
  LanguageCode,
  generateUrl,
  getDataCenter,
  getLocalesForDataCenter,
  IDataCenter,
  Locale,
  DataCenterCode,
} from 'utils/Locales';

type SameDataCenterLocaleProps = {
  locale: Locale;
};

// filters locale information & empty strings from the url path and returns the slug
export const removeLocalesFromPath = (path: string) => {
  const countryCodeList = Object.values(CountryCode).map(each => each.toLowerCase());
  const languageCodeList = Object.values(LanguageCode);
  // creates an array of the current path, omitting empty strings
  const urlPathAsArray = path.split('/').filter(Boolean);
  return urlPathAsArray
    .filter((each, index, array) => {
      // locale information is prepended so only run the check if it's the first or second index
      if (index === 0) return !countryCodeList.includes(each as CountryCode);
      if (index === 1)
        return (
          !languageCodeList.includes(each as LanguageCode) ||
          !countryCodeList.includes(array[0] as CountryCode)
        );
      return true;
    })
    .join()
    .replace(/,/g, '/');
};

export const SameDataCenterLocale: React.FunctionComponent<SameDataCenterLocaleProps> = ({
  locale: l,
  children,
}) => {
  let baseUrl = useBaseUrlFromLocale(l.code) as string;
  if (baseUrl === '') baseUrl = '/';

  return (
    <a href={baseUrl} title={l.displayName} data-testid={`${l.code.toLowerCase()}-locale-selector`}>
      {children}
    </a>
  );
};

type DifferentDataCenterLocaleProps = {
  locale: Locale;
};

export const DifferentDataCenterLocale: React.FunctionComponent<DifferentDataCenterLocaleProps> = ({
  locale,
  children,
}) => {
  // Add the locale code to the query params so the correct locale will be selected after going to the href
  return (
    <a
      href={generateUrl(locale)}
      title={locale.displayName}
      data-testid={`${locale.code.toLowerCase()}-locale-selector`}
    >
      {children}
    </a>
  );
};

export interface IRegionProps {
  dataCenter: IDataCenter;
  closeModal: () => void;
}

export class Region extends React.PureComponent<IRegionProps> {
  render() {
    const { dataCenter: dc } = this.props;
    const localesInDC = getLocalesForDataCenter(dc);

    if (!localesInDC.length) return null; // Don't render this data center block if there are no locales

    // Use always same domain for NA locales
    const isNALocale = dc.code === DataCenterCode.NORTH_AMERICA;

    // Use an anchor tag to redirect the user if the locales are in a different data center
    const key: keyof IDataCenter =
      (Environment.current.isCloverProdEnvironment && 'prodHost') ||
      (Environment.current.isCloverStagingEnvironment && 'stgHost') ||
      'devHost';
    const isCurrentDataCenter = isNALocale || getDataCenter()[key] === dc[key];

    return (
      <div className={styles.region} key={dc.code}>
        <h4 className={styles.regionTitle}>{dc.displayName}</h4>
        <ul>
          {localesInDC.map(l => (
            <li key={l.code}>
              {isCurrentDataCenter && (!l.country.subPathOverride || l.country.subPathOverride) ? (
                <SameDataCenterLocale locale={l}>
                  <Image aria-hidden src={l.country.icon} />
                  <span lang={l.code}>{l.displayName}</span>
                </SameDataCenterLocale>
              ) : (
                <DifferentDataCenterLocale locale={l}>
                  <Image aria-hidden src={l.country.icon} />
                  <span lang={l.code}>{l.displayName}</span>
                </DifferentDataCenterLocale>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
}
